import { TbIcon } from 'components/Figma';
import { FormattedMessage } from 'react-intl';
import { MeasureComparisonUtil } from './measure-comparison/MeasureComparisonUtil';
import { MeasureUnitInfo } from './measure-unit/MeasureUnitInfo';
import { MeasureUnitCode } from './measure-unit/MeasureUnitCode';
import { MeasureFormat } from './measure-unit/MeasureFormat';
import { getChipSxStylingForLevel } from 'styles/utils';
import { Chip, Typography, useTheme } from '@mui/material';

/**
 *  Interpretation will be 1 or -1 dependent on whether an increase in value is
 *  seen as positive (1) or negative (-1)
 */
export class Interpretation {
  private static NEGATIVE_INTERPRETATION = -1;

  private static POSITIVE_INTERPRETATION = 1;

  static get NEGATIVE() {
    return this.NEGATIVE_INTERPRETATION;
  }

  static get POSITIVE() {
    return this.POSITIVE_INTERPRETATION;
  }
}

const useComparisonTheme = (
  value: number,
  comparisonValue: number | null,
  interpretation: number,
  unit: MeasureUnitCode | MeasureUnitInfo
) => {
  const theme = useTheme();

  if (comparisonValue === null) {
    return {
      sx: getChipSxStylingForLevel('Default'),
      icon: null,
    };
  }
  // interpretation will be 1 or -1 dependent on whether an increase is seen as positive (1) or negative(-1)
  // multiplying the value with the comparison will change the direction therefor if needed.
  const positiveInterpretation = value * interpretation > comparisonValue * interpretation;

  if (unit === MeasureUnitCode.RATE) {
    return {
      sx: getChipSxStylingForLevel(interpretation > 0 ? 'Success' : 'Critical'),
    };
  }

  const comparisonTextColor = positiveInterpretation ? theme.palette.success.main : theme.palette.error.main;

  let sx;
  if (value === comparisonValue) {
    sx = getChipSxStylingForLevel('Secondary');
  } else {
    sx = getChipSxStylingForLevel(positiveInterpretation ? 'Success' : 'Critical');
  }

  if (value < comparisonValue) {
    return {
      sx: sx,
      icon: <TbIcon name="TrendDown" size="xs" color={comparisonTextColor} />,
    };
  }
  if (value > comparisonValue) {
    return {
      sx: sx,
      icon: <TbIcon name="TrendUp" size="xs" color={comparisonTextColor} />,
    };
  }
  return {
    sx: sx,
    icon: <TbIcon name="TrendFlat" size="xs" />,
  };
};

export function MeasureComparison({
  value,
  comparisonValue,
  unit,
  interpretation,
  size = 'small',
  // use the new calcGrowth calculation dealing with 0->0 and infinity growths, false by default to maintain current
  // functionality but hope is to make this the default
  useCalcGrowth = false,
}: {
  value: number;
  comparisonValue: number | null;
  unit: MeasureUnitCode | MeasureUnitInfo;
  interpretation: number;
  size?: 'small' | 'medium';
  useCalcGrowth?: boolean;
  className?: string;
}) {
  const theme = useComparisonTheme(value, comparisonValue, interpretation, unit);
  const unitCode = typeof unit === 'string' ? unit : unit.code;

  if (comparisonValue === null) {
    return (
      <Typography variant="caption2" component="span">
        <FormattedMessage id="measurecomparison.nodata" defaultMessage="no data" />
      </Typography>
    );
  }

  const determineDisplayValue = () => {
    if (unitCode === MeasureUnitCode.PERCENT) {
      return (
        <MeasureFormat
          value={MeasureComparisonUtil.calcValue(value, comparisonValue)}
          unit={unitCode}
          config={{ isDiff: true, hasSign: true }}
        />
      );
    }
    if (unitCode === MeasureUnitCode.RATE) {
      return (
        <MeasureFormat
          value={value}
          unit={unitCode}
          config={{ hasSign: false, removeDecimalZeros: true }}
          rate={comparisonValue}
        />
      );
    }
    if (unitCode === MeasureUnitCode.COUNT) {
      return (
        <MeasureFormat
          value={value - comparisonValue}
          unit={unitCode}
          config={{ hasSign: true, removeDecimalZeros: true }}
          rate={comparisonValue}
        />
      );
    }
    return (
      <MeasureFormat
        value={Math.abs(MeasureComparisonUtil.calcPercentage(value, comparisonValue, useCalcGrowth) ?? 0)}
        unit={MeasureUnitCode.PERCENT}
        config={{ hasSign: false }}
      />
    );
  };

  const displayValue = determineDisplayValue();

  return (
    <Typography variant="caption2" component="span" fontWeight="300" lineHeight="22px">
      {theme.icon && <Chip size={size} sx={theme.sx} icon={theme.icon} label={displayValue} />}
      {!theme.icon && <Chip className={size} sx={theme.sx} label={displayValue} />}
    </Typography>
  );
}
